import { memo, useEffect, useState } from "react";
import type { VirtualItem } from "@tanstack/react-virtual";
import { TaskSummaryCard } from "@/components/tasks/TaskSummaryCard";
import type { TaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import type { TaskExtended } from "@/types";

// Kanban card = a shared `TaskSummaryCard` wrapped with the virtualiser's
// absolute-positioning shell. The outer div is the measured node;
// `data-index` is required by `measureElement`. Memoised because a scroll
// event that only changes `virtualItem.start` would otherwise re-render
// every visible card.
//
// The shell's translateY is scroll-independent — it only moves when the list
// itself changes, e.g. a card leaves the column and the ones below close the
// gap. Transitioning transform therefore animates exactly those shifts and
// never fights scrolling.
export const KanbanTaskCard = memo(function KanbanTaskCard({
  task,
  projectNameById,
  plotNameById,
  onClick,
  onToggleCompleted,
  measureRef,
  virtualItem,
}: {
  task: TaskExtended;
  projectNameById: Map<string, string>;
  plotNameById: Map<string, string>;
  onClick: () => void;
  onToggleCompleted?: TaskCompletionToggle;
  measureRef: (node: Element | null) => void;
  virtualItem: VirtualItem;
}) {
  // Held off until after mount so the virtualiser's first measurement pass —
  // which corrects every offset away from the 160px estimate — snaps into
  // place instead of sliding down the column on open.
  const [canAnimate, setCanAnimate] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setCanAnimate(true), 250);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      ref={measureRef}
      data-index={virtualItem.index}
      className={
        canAnimate
          ? "motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out"
          : undefined
      }
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      <div className="pb-3">
        <TaskSummaryCard
          task={task}
          projectNameById={projectNameById}
          plotNameById={plotNameById}
          onClick={onClick}
          onToggleCompleted={onToggleCompleted}
        />
      </div>
    </div>
  );
});
